
import React, { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { CERTIFICATE_STATUSES } from 'constants/statuses'

import { date } from 'utils'

import { useReferrerState } from 'hooks'
import { useCertificateMutations, useCertificateTriggerSettings, useCurrentUserCompany, useHasArrivedAtLocation } from 'hooks/api'
import { activationTriggerTitle } from 'constants/activationTriggers'
import { resolveAccessSetting, resolveActivationTrigger } from '@repo/types'

import type { AccessSettingKey } from 'constants/accessSettings'
// ComponentIcon dropped with the Tag field's tagless-parent branch (VRG-231).
// PencilIcon dropped with the manufacture-date Edit Button (VRG-517) — the pencil is now
// `InlineEditButton`'s own default, so this file no longer names a glyph.
import { ChevronDown, InfoIcon, ShoppingCartIcon } from 'lucide-react'
import { ROUTES } from 'routes'
import { CertificateAdmin } from 'types'
import type { CertificateGroupData } from 'hooks/api'
import type { CertificateGroupRole } from 'components/display/CertificateGroupMemberPreview'
import type { ActionDropdownItem } from 'components/input/ActionDropdown'

import { Box, Stack, Typography, Grid, AccordionDetails, AccordionSummary, Accordion, Link, Tooltip, useTheme } from '@mui/material'

// From @repo/ui, not @mui/material (working-conventions §47).
//
// ⚠️ The comment here used to say "the live Buttons are the manufacture-date Edit and the
// group 'Add Next Group' action". That is no longer true and was already half-wrong: the
// manufacture-date Edit became an `InlineEditButton` (VRG-517), and there is no live "Add
// Next Group" Button in this file. `Button`, `Image` and `Card` are now referenced ONLY by
// the commented-out card at the foot of this file — retained so uncommenting it still
// compiles, not because anything renders them.
import { Button, Image, Card, SURFACE_RADIUS, InlineEditButton } from '@repo/ui'
import {
  StatusChip,
  Slideshow,
  InfoBlock,
  InfoItem,
  InfoRow,
  ProductCell,
  CertificateOwnerCell,
  EditManufacturedAtModal,
  EditActivationTriggerModal,
  ActivationTriggerItem,
  EditAccessSettingModal,
  AccessSettingItem,
  PermissionGate,
  CertificateGroupMemberPreview,
  CertificateGroupMemberPreviewSkeleton,
  GroupMemberList,
} from 'components'

interface ProductProps {
  certificate: CertificateAdmin
  refetch?: () => void
  /** Undefined = standalone certificate — no group sections render at all. */
  groupRole?: CertificateGroupRole
  groupData: CertificateGroupData | null
  isGroupLoading: boolean
  getGroupMemberActionItems: (row: CertificateAdmin) => ActionDropdownItem[]
}

// "Children" / "Parent" / "Related" section heading — plain title + info tooltip,
// matching the @repo/ui Card title treatment without wrapping each member row in an
// extra outer card (the brief calls for ONE card per member, not one card per section).
const GroupSectionHeading = ({ title, tooltip }: { title: string; tooltip: string }) => {
  const theme = useTheme()
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Tooltip title={tooltip}>
        <Box sx={{ display: 'inline-flex', cursor: 'pointer' }}>
          <InfoIcon size={16} strokeWidth={1} color={theme.palette.primary[400]} />
        </Box>
      </Tooltip>
    </Stack>
  )
}

// VRG-231 review item 8 — the "L" tree/indent graphic (GroupMemberList) that used
// to live here directly now lives in components/display/CertificateGroupMemberPreview
// so SplitGroupModal can reuse the SAME graphic (§36) instead of rebuilding a
// second one. See that file for the implementation notes.

export const Product = ({
  certificate,
  refetch,
  groupRole,
  groupData,
  isGroupLoading,
  getGroupMemberActionItems,
}: ProductProps) => {
  const theme = useTheme()
  const [openEditManufacturedAt, setOpenEditManufacturedAt] = useState(false)
  // VRG-528 — the per-certificate activation-trigger override.
  const [openActivation, setOpenActivation] = useState(false)
  /**
   * VRG-591 — WHICH access flag is being edited, or null for "no modal". One piece
   * of state rather than two booleans, so "both modals open" is not representable.
   */
  const [editingAccessSetting, setEditingAccessSetting] = useState<AccessSettingKey | null>(null)
  /*
    Has this certificate already ARRIVED somewhere? The client's honest signal is the
    provenance chain, which gains a store row the moment a hop completes — so this
    reads the same admin provenance the tab renders rather than inventing a second
    endpoint for one boolean.
  */
  const {hasArrived: hasArrivedAtLocation} = useHasArrivedAtLocation(
    certificate.id,
    // Only needed while the modal can be opened; the answer just changes wording.
    {enabled: openActivation},
  )
  const companyQuery = useCurrentUserCompany()
  const companyId = companyQuery.data?.id ?? ''
  const { settings: triggerSettings } = useCertificateTriggerSettings(companyId, {
    enabled: !!companyId,
  })
  // What this certificate falls back to when it has no override — hoisted because
  // the picker needs it BOTH as a title and as an id (the id is what carries the
  // trigger's glyph onto the inherit row). Resolving it twice inline would be two
  // places to keep in step for one value.
  const inheritedTrigger = resolveActivationTrigger({
    product: certificate.product?.activation_trigger ?? null,
    company: triggerSettings?.activation_trigger ?? null,
  }).trigger
  const [{ setActivationTrigger, activate }, { isSettingActivationTrigger }] =
    useCertificateMutations({
      onSuccess: () => {
        refetch?.()
        setOpenActivation(false)
      },
    })

  // VRG-591 — its own hook instance so its `onSuccess` closes the ACCESS modal
  // rather than the activation one.
  const [{ setAccessSetting }, { isSettingAccessSetting }] = useCertificateMutations({
    onSuccess: () => {
      refetch?.()
      setEditingAccessSetting(null)
    },
  })
  // Detail → detail: the derived label would be the Certificates list, not this record.
  const referrer = useReferrerState('Certificate')

  return (
    <>
    <Grid container spacing={6}>
      <Grid item xs={12} md={5}>
        <Slideshow images={certificate.product.images} />
      </Grid>

      <Grid item xs={12} md={7}>
        <Stack spacing={4}>
          <InfoBlock>
            <InfoRow>
              <InfoItem label="Certificate ID" term="certificate" value={certificate.id} />
              {/*
                `term`, not a literal tooltip (§44). "Creation date" was one of the
                last two rows on this card carrying no `ⓘ`, and it is the one most
                likely to be misread: three of the dates in this block look
                interchangeable at a glance, and this is the only one that describes
                the RECORD rather than the object. The entry says so once, and the
                activation row beside it and the docs read the same sentence.
              */}
              <InfoItem
                label="Creation date"
                term="certificate-creation-date"
                value={date(certificate.created_at)}
              />
            </InfoRow>
            <InfoRow>
              <InfoItem label="Product" term="product" value={
                <Link component={RouterLink} to={ROUTES.PRODUCT(certificate.product.id)} state={referrer}>{certificate.product.title}</Link>
              }/>
              {/*
                `term`, not a literal tooltip (§44), and the MIRROR of the one on
                "Item manufacture date" two rows down. The pair is the most-reported
                ambiguity on this card — two near-identically-named dates, one of
                which silently stands in for the other in provenance. Neither row can
                carry that on its own, so each names the other's scope and the
                glossary states the fallback direction once.
              */}
              <InfoItem
                label="Product manufacture date"
                term="product-manufacture-date"
                value={date(certificate.product.manufactured_at)}
              />
            </InfoRow>
            <InfoRow>
              {/*
                VRG-528 — the current owner.

                Reads `certificate` directly. It briefly could NOT: the detail page
                loaded through the OptionalAuth public endpoint, which served a brand
                admin the same short payload an anonymous scanner gets, with `customer`
                nulled because `show_ownership` is the BUYER's toggle — so the brand was
                denied the owner of a certificate it issued, by a switch it does not
                control, while the same owner sat one endpoint away on the certificates
                table. The page now loads `GET /certificates/admin/:id`, so the field is
                simply here, and the second request that used to fetch it is gone.

                `CertificateOwnerCell` is the same component the certificates table
                renders, so the two surfaces cannot disagree about who owns a thing —
                including the brand-held state, where the answer is the company and
                not a blank.

                PROMOTED to this row, swapping places with Activation date below, so
                that the activation TRIGGER and the activation DATE it produces sit on
                one row: the setting says when a certificate activates, the date says
                when it did, and an operator reading either almost always wants the
                other. Owner is a different subject and was wedged between them.
              */}
              <InfoItem
                label="Current owner"
                term="owner"
                valueSpacing={3}
                value={
                  <CertificateOwnerCell
                    certificate={certificate}
                    company={companyQuery.data}
                  />
                }
              />
              {/*
                `term`, not a literal tooltip (§44). This row was one of the ones in
                this card carrying no `ⓘ` at all, and it is the one that most needed
                one: the row directly above is "Product manufacture date", and nothing
                on screen said which of the two dates belongs to the design and which
                to the single item in your hand. The glossary entry defines the
                distinction once, so this row and the docs cannot answer it
                differently.
              */}
              <InfoItem
                label="Item manufacture date"
                term="item-manufacture-date"
                value={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.25 }}>
                      {date(certificate.manufactured_at ?? '') || 'Not set'}
                    </Typography>
                    {/*
                      `InlineEditButton`, not a `variant="secondary"` Button (VRG-517). This
                      slot is a handle on ONE value sitting inside an `InfoItem`, and a 40px
                      bordered, filled control measured physically larger than the date it
                      edits — the affordance out-weighed its own subject. The shared control
                      draws nothing at rest. See its docblock in `@repo/ui` for why 32px here
                      is not a working-conventions §60 violation.
                    */}
                    <PermissionGate permission="manage_certificate">
                      <InlineEditButton
                        label="Edit"
                        onClick={() => setOpenEditManufacturedAt(true)}
                        aria-label="Edit item manufacture date"
                      />
                    </PermissionGate>
                  </Stack>
                }
              />
            </InfoRow>
            <InfoRow>
              <ActivationTriggerItem
                scope="certificate"
                own={certificate.activation_trigger ?? null}
                product={certificate.product?.activation_trigger ?? null}
                company={triggerSettings?.activation_trigger ?? null}
                productId={certificate.product?.id}
                onEdit={() => setOpenActivation(true)}
              />
              {/*
                VRG-231 — this slot used to be "Tag", rendering the raw chip UID. That is not
                something the product exposes, and the default-tag surface already covers it,
                so it is replaced by the certificate's activation date.

                The tagless-parent special case that lived here went WITH the Tag field, and
                deliberately does not carry over: a group PARENT has no chip of its own (a
                structural fact worth explaining), but it activates exactly like any other
                certificate — CreateCertificateGroupUseCase mirrors the children's status onto
                it, and ActivateCertificateUseCase's indivisible cascade updates the parent row
                itself. So a parent's activation date is a real value, not an absence, and
                special-casing it here would invent a distinction the data does not have.

                "Not recorded", NOT "-" and NOT "Not set":
                  * "-" on an ACTIVE certificate reads as missing/ignorable, which is exactly
                    the wrong signal for the twelve already-active certificates that predate
                    the column and can never be truthfully backfilled.
                  * "Not set" (the Item-manufacture-date idiom, row above) implies something a user
                    could go and set. Nothing writes this by hand; the server stamps it on the
                    activation transition.
                  * "Not activated" would be a lie for those same pre-existing active rows.
                "Not recorded" is the one phrasing that is TRUE in both cases — for a
                certificate that has not activated yet, and for one that activated before
                anything was recording it.
              */}
              <InfoItem
                label="Activation date"
                term="activation"
                value={date(certificate.activated_at ?? '') || 'Not recorded'}
              />
            </InfoRow>
            {/*
              VRG-591 — the two certificate-access flags, each resolving its own
              chain (certificate -> product -> company) and each naming the level its
              value came from. Two rows rather than one bundled cell because the two
              genuinely resolve independently: email can come from the product while
              location comes from the company, and one row cannot show both sources.
            */}
            <InfoRow>
              <AccessSettingItem
                setting="collect_email"
                scope="certificate"
                own={certificate.collect_email ?? null}
                product={certificate.product?.collect_email ?? null}
                company={triggerSettings?.collect_email ?? null}
                productId={certificate.product?.id}
                onEdit={() => setEditingAccessSetting('collect_email')}
              />
              <AccessSettingItem
                setting="collect_location"
                scope="certificate"
                own={certificate.collect_location ?? null}
                product={certificate.product?.collect_location ?? null}
                company={triggerSettings?.collect_location ?? null}
                productId={certificate.product?.id}
                onEdit={() => setEditingAccessSetting('collect_location')}
              />
            </InfoRow>
          </InfoBlock>

          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              '&:hover': {
                // backgroundColor: theme.palette.primary[50],
                borderRadius: `${SURFACE_RADIUS}px`
              },
              '&:before': {
                display: 'none'
              },
              // SURFACE_RADIUS, not a literal — this accordion sits directly under the
              // InfoBlock and beside the product image, and it was the 8px outlier that
              // every other card copied while commenting that it matched them.
              borderRadius: `${SURFACE_RADIUS}px`,
              // MUI re-rounds an Accordion's first/last corners to shape.borderRadius
              // (4px) at higher specificity than a plain sx class, so without this the
              // panel renders 12px on top and 4px on the bottom.
              '&:first-of-type, &:last-of-type': { borderRadius: `${SURFACE_RADIUS}px` },
              border: `1px solid ${theme.palette.primary[100]}`,
              // Keep MUI's expanded TOP margin (breathing room above an open panel)
              // but drop the BOTTOM margin — that's what grew the gap to the next
              // accordion on open.
              '&.Mui-expanded': { marginBottom: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ChevronDown strokeWidth={1} size={18} />}
              aria-controls="product-description"
              id="product-description"
              sx={{
                flexDirection: 'row-reverse',
                pr: 3,
                pl: 3,
                borderBottom: 'none',
                minHeight: 56,
                '& .MuiAccordionSummary-content': {
                  margin: 3,
                  // Pin the expanded margin to the collapsed value — MUI's default
                  // .Mui-expanded uses `margin: 20px 0`, which drops the horizontal
                  // margin and slides the title left on open.
                  '&.Mui-expanded': { margin: 3 },
                }
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight={500}>Description</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{px: 3, pb: 6, pt: 0,  pl: 10.5}}>
              {certificate.product?.description ? (
                <div
                  dangerouslySetInnerHTML={{ __html: certificate.product.description }}
                  style={{ lineHeight: 1.6, color: theme.palette.text.primary }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No description available
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          {/* VRG-234 — group cross-references. A standalone certificate has neither
              `parent_certificate_id` nor `group_divisibility` set, so `groupRole` is
              undefined and NONE of these render — no empty shells. VRG-231 review
              item 2: moved below Description/Details (was above) so the section
              order matches the review's requested layout. */}
          {groupRole === 'parent' && (
            // VRG-231 review item 4 — extra top margin (on top of the outer Stack's
            // spacing={4}) so the gap down from the Details accordion reads as its
            // own section break rather than just another list item.
            // VRG-231 review round 2 item 7 — was `mt: 4` (16px), which still read as
            // too tight against the Details accordion above. Platform is on the
            // @repo/ui 4px base (§29), so the target is 24px.
            //
            // ⚠️ VRG-231 review round 3 — `mt` DOES NOT WORK HERE and the `mt: 6`
            // this replaces was inert. This Stack is a direct child of an outer
            // `<Stack spacing={4}>`, and MUI implements that spacing as
            // `& > :not(style) + :not(style) { margin-top: 16px }` on the PARENT —
            // a higher-specificity selector than the child's own `sx` margin, so
            // the parent wins and the computed value stayed 16px no matter what
            // number was written here. Measured in the browser: Description
            // accordion bottom 633 -> "Children" heading top 649 = 16px, with
            // `mt: 6` in the source. Padding is not part of that selector, so it
            // composes with the inherited margin instead of losing to it:
            // 16px (parent spacing) + 8px (pt) = the 24px intended all along.
            // Re-measure rather than trusting the token if this is touched again.
            <Stack spacing={3} sx={{ pt: 2 }}>
              <GroupSectionHeading
                title="Children"
                tooltip="The tagged components that make up this group."
              />
              <GroupMemberList>
                {isGroupLoading ? (
                  // Array literal, not a Fragment — GroupMemberList's line-drawing
                  // relies on React.Children.toArray flattening its children so
                  // each row gets its own connector; a <>...</> Fragment would count
                  // as a single child and only draw one connector for both rows.
                  [
                    <CertificateGroupMemberPreviewSkeleton key="skeleton-1" />,
                    <CertificateGroupMemberPreviewSkeleton key="skeleton-2" />,
                  ]
                ) : (
                  (groupData?.children ?? []).map(child => (
                    <CertificateGroupMemberPreview
                      key={child.id}
                      certificate={child}
                      role="child"
                      actionItems={getGroupMemberActionItems(child)}
                    />
                  ))
                )}
              </GroupMemberList>
            </Stack>
          )}

          {groupRole === 'child' && (
            // VRG-231 review item 4 — same extra top margin as the Children section
            // above (this is the other section that directly follows the Details
            // accordion, on a child's own page). VRG-231 review round 2 item 7 — kept
            // in parity with the Children section above, which means `pt`, not `mt`:
            // this Stack is a sibling in the same `spacing={4}` parent, so its own
            // margin loses to the parent's spacing selector. See the long note on
            // the Children section for the measurement.
            <Stack spacing={3} sx={{ pt: 2 }}>
              <GroupSectionHeading
                title="Parent"
                tooltip="The tagless certificate this component belongs to."
              />
              {/* connector={false} — the Parent/Related members are not BELOW this
                  certificate, so the descending tree line would draw the hierarchy
                  wrong (Parent) or invent one that does not exist (Related). See
                  GroupMemberList's own `connector` doc. */}
              <GroupMemberList connector={false}>
                {isGroupLoading ? (
                  <CertificateGroupMemberPreviewSkeleton />
                ) : groupData?.parent ? (
                  <CertificateGroupMemberPreview
                    certificate={groupData.parent}
                    role="parent"
                    actionItems={getGroupMemberActionItems(groupData.parent)}
                  />
                ) : null}
              </GroupMemberList>
            </Stack>
          )}

          {groupRole === 'child' && (isGroupLoading || (groupData?.siblings?.length ?? 0) > 0) && (
            <Stack spacing={3}>
              <GroupSectionHeading
                title="Related"
                tooltip="The other components in the same group."
              />
              {/* connector={false} — the Parent/Related members are not BELOW this
                  certificate, so the descending tree line would draw the hierarchy
                  wrong (Parent) or invent one that does not exist (Related). See
                  GroupMemberList's own `connector` doc. */}
              <GroupMemberList connector={false}>
                {isGroupLoading ? (
                  <CertificateGroupMemberPreviewSkeleton />
                ) : (
                  (groupData?.siblings ?? []).map(sibling => (
                    <CertificateGroupMemberPreview
                      key={sibling.id}
                      certificate={sibling}
                      role="child"
                      actionItems={getGroupMemberActionItems(sibling)}
                    />
                  ))
                )}
              </GroupMemberList>
            </Stack>
          )}
          </Stack>
      </Grid>
  </Grid>

    <EditActivationTriggerModal
      open={openActivation}
      onClose={() => setOpenActivation(false)}
      scope="certificate"
      current={certificate.activation_trigger ?? null}
      inheritedTitle={activationTriggerTitle(inheritedTrigger)}
      inheritedTrigger={inheritedTrigger}
      loading={isSettingActivationTrigger}
      // The rule cannot fire for an already-live certificate; the modal says so
      // rather than silently storing something inert.
      alreadyActivated={Boolean(certificate.activated_at)}
      /*
        A RECEIVED hop exists for this certificate, so "On location arrival" names a moment
        already in the past. `useCertificateCustodyHistory` is not a thing — the
        signal we have client-side is the provenance chain, which already contains a
        store row once a hop completes.
      */
      alreadyArrived={hasArrivedAtLocation}
      /*
        SEQUENCED, not concurrent. When the chosen moment has already passed the
        setting is saved AND the certificate activated — but the activate only fires
        once the save has actually landed. Firing both together ran two writes and
        two refetches at the same row, and whichever refetch resolved last won the
        cache, so the row could come back showing the value from before the save.
      */
      onSave={(next, opts) =>
        setActivationTrigger(
          {
            certificateId: certificate.id,
            activation_trigger: next,
          },
          opts?.activateAfter
            ? {onSuccess: () => activate({certificateId: certificate.id})}
            : undefined,
        )
      }
    />

    {/*
      VRG-591 — mounted only while a flag is being edited, so `current` and
      `inheritedValue` are always read for THAT flag.
    */}
    {editingAccessSetting && (
      <EditAccessSettingModal
        open
        onClose={() => setEditingAccessSetting(null)}
        setting={editingAccessSetting}
        scope="certificate"
        current={certificate[editingAccessSetting] ?? null}
        // What this certificate falls back to with no override of its own — the
        // shared resolver, over the product then the company, so the modal's
        // inherit row cannot disagree with the row behind it.
        inheritedValue={
          resolveAccessSetting(
            {
              product: certificate.product?.[editingAccessSetting] ?? null,
              company: triggerSettings?.[editingAccessSetting] ?? null,
            },
            true,
          ).value
        }
        loading={isSettingAccessSetting}
        onSave={next =>
          setAccessSetting({
            certificateId: certificate.id,
            [editingAccessSetting]: next,
          } as Parameters<typeof setAccessSetting>[0])
        }
      />
    )}

    <EditManufacturedAtModal
      open={openEditManufacturedAt}
      onClose={() => setOpenEditManufacturedAt(false)}
      certificate={certificate}
      refetch={refetch}
    />
    </>
  )
}


{/* <Card sx={{mt: 24, p: 4, borderRadius: 2}}>
  <Stack spacing={6} direction="row" alignItems="center" justifyContent="space-between">
    <Stack spacing={3} direction="row" alignItems="center">
      <Image
        src={certificate?.images[0] as string}
        alt={certificate?.title}
        fit="cover"
        sx={{ width: 64, height: 64, borderRadius: 2 }}
      />
      <Typography variant="body2" noWrap>
        {certificate.title}
      </Typography>
    </Stack>
    <Typography variant="body2" noWrap>
      {certificate.title} in inventory
    </Typography>
    <Button 
      variant="outlined"
      size="medium"
      disableElevation
      disableRipple
      sx={{borderRadius: 2, p: 1, pr: 4, pl: 4}}
      onClick={() => navigate('./'+ ROUTES.TAGS_SHOP_PRODUCT(certificate.id))}
      startIcon={<ShoppingCartIcon strokeWidth={1}/>}
    >Order More</Button>
  </Stack>
</Card> */}